import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AdminAccessService } from '../services/admin-access.service';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface LoginResponse {
  accessToken:  string;
  refreshToken: string;
  role:         'admin' | 'school' | 'student';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY   = 'isso_access_token';
  private readonly REFRESH_KEY = 'isso_refresh_token';
  private readonly ROLE_KEY    = 'isso_role';

  constructor(private http: HttpClient, private router: Router, private access: AdminAccessService) {}

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.baseUrl}auth/login`, { email, password }).pipe(
      map(res => {
        this.storeSession(res);
        if (res.role === 'admin') {
          // The nav and ModuleGuard read this; land on a module this account
          // actually holds, which for a super admin is still the dashboard.
          this.access.refresh(
            () => this.router.navigate([this.access.homeRoute()]),
            // Access unknown: the dashboard is where every admin landed before,
            // and the server re-checks each call regardless.
            () => this.router.navigate(['/admin/dashboard']),
          );
        } else if (res.role === 'school') {
          this.router.navigate(['/staffadmin/event-dashboard']);
        } else {
          this.router.navigate(['/login']);
        }
        return res;
      }),
      catchError(err => throwError((err.error && err.error.error) || 'Login failed')),
    );
  }

  refresh(): Observable<string> {
    const refreshToken = localStorage.getItem(this.REFRESH_KEY);
    return this.http.post<{ accessToken: string }>(`${environment.baseUrl}auth/refresh`, { refreshToken }).pipe(
      map(res => {
        localStorage.setItem(this.TOKEN_KEY, res.accessToken);
        return res.accessToken;
      }),
    );
  }

  logout(): void {
    this.access.clear();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
    localStorage.removeItem(this.ROLE_KEY);
    localStorage.removeItem('roleId');
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(this.TOKEN_KEY);
  }

  getRole(): string | null {
    return localStorage.getItem(this.ROLE_KEY);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Adopt a session handed over by the web portal's /login page.
   *
   * The portal runs on a different origin, so the tokens it holds are invisible
   * to this app - they arrive in the URL fragment (never sent to a server) and
   * are moved into localStorage here, then wiped from the address bar. Without
   * this the admin lands signed-out-but-looking-signed-in and every authenticated
   * call 401s.
   */
  adoptSessionFromUrl(): boolean {
    const hash = window.location.hash || '';
    if (hash.indexOf('access_token=') === -1) return false;

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken  = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const role         = params.get('role') as LoginResponse['role'];
    // Drop the fragment before anything else can read or bookmark it.
    history.replaceState(null, '', window.location.pathname + window.location.search);

    if (!accessToken || role !== 'admin') return false;
    this.storeSession({ accessToken, refreshToken: refreshToken || '', role });
    return true;
  }

  private storeSession(res: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, res.accessToken);
    localStorage.setItem(this.REFRESH_KEY, res.refreshToken);
    localStorage.setItem(this.ROLE_KEY, res.role);
    // Legacy key expected by permission.guard.ts: admin=1, school=2
    const roleId = res.role === 'admin' ? '1' : res.role === 'school' ? '2' : '3';
    localStorage.setItem('roleId', roleId);
  }
}
